import { CONFIGURATION_STATUS_TYPE, PROCESS_TYPE } from 'src/shared/constants/constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Option } from 'src/shared/form/control'
import React, { useMemo } from 'react'
import { RadioButtonGroupField, RadioData } from 'src/shared/form/control/RadioButtonGroupField'

import { Form } from 'react-final-form'
import { Alert, Grid2, Stack, Typography } from '@mui/material'
import { required } from 'src/shared/form/validation/validators'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { TextField } from 'src/shared/form/control/TextField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { getConfigStatusOptions } from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { getCriteriaConfigProcessOptions } from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import { createDecorators } from 'src/shared/form/utils/decorators'

export interface CommitteeConfigurationFormValue {
  enDesc: string
  deDesc: string
  frDesc: string
  itDesc: string
  status: CONFIGURATION_STATUS_TYPE
  process: PROCESS_TYPE
}

interface CommitteeConfigurationFormProps {
  mode: 'EDIT' | 'CREATE'
  onSubmit: (values: CommitteeConfigurationFormValue) => Promise<void>
  initialValues: CommitteeConfigurationFormValue
  formDirty?: boolean
}

export const CommitteeConfigurationForm = ({
  mode,
  onSubmit,
  initialValues,
  formDirty,
}: CommitteeConfigurationFormProps) => {
  const { getMessage } = useMessageSource()
  const decorators = createDecorators()

  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])
  const processOptions: RadioData[] = useMemo(() => getCriteriaConfigProcessOptions(getMessage), [getMessage])

  return (
    <Stack spacing={3}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      <Form<CommitteeConfigurationFormValue>
        initialValues={initialValues}
        onSubmit={onSubmit}
        decorators={decorators}
        render={({ handleSubmit }) => {
          return (
            <form id="committee-config-form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={4}>
                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={statusFieldOptions}
                      name="status"
                      label={getMessage('label.status')}
                      disableClearable={true}
                      required
                      validate={required()}
                    />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">{getMessage('label.description')}</Typography>

                <Grid2 container columnSpacing={5}>
                  <Grid2 size={6}>
                    <TextField
                      name="deDesc"
                      label={getMessage('label.description.de')}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField
                      name="enDesc"
                      label={getMessage('label.description.en')}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField
                      name="frDesc"
                      label={getMessage('label.description.fr')}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField
                      name="itDesc"
                      label={getMessage('label.description.it')}
                      required
                      validate={required()}
                    />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">{getMessage('label.configuration.grid.allocation')}</Typography>

                <Grid2 container columnSpacing={5}>
                  <RadioButtonGroupField
                    name="process"
                    color="primary"
                    data={processOptions}
                    required
                    label={
                      <Typography variant={'subtitle2'} component="span" sx={{ ml: -2 }}>
                        {getMessage('label.process')}
                      </Typography>
                    }
                    disabled={mode !== 'CREATE'}
                  />
                </Grid2>

                <DirtyFormSpy gridDirty={formDirty} />
              </Stack>
            </form>
          )
        }}
      />
    </Stack>
  )
}
